import { useAtomValue } from "jotai";
import { useRef } from "react";

import { Accordion, ScrollArea } from "@go-novus/ui";

import {
  knowledgeQuestionsAtom,
  readKnowledgeFailureMessage,
} from "#/features/knowledge";
import * as m from "#/paraglide/messages";
import { useFailureNotice } from "#/shared/ui/failure-notice";
import { InfiniteScroll } from "#/shared/ui/infinite-scroll";

import { KnowledgeNotice } from "./knowledge-notice";

const FAILURE_ICON = "info";

/** Online 问答只读展开；全文保持换行，长单词和 URL 在窄屏内换行。 */
export function QuestionsSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const list = useAtomValue(knowledgeQuestionsAtom);
  const items = list.data?.pages.flatMap((page) => page.items) ?? [];
  const failure = useFailureNotice({
    failedAt: list.errorUpdatedAt,
    hasContent: items.length > 0,
    message: list.isError
      ? (readKnowledgeFailureMessage(list.error) ??
        m.knowledge_failure_unexpected())
      : null,
  });
  return (
    <ScrollArea
      height="auto"
      label={m.knowledge_tab_questions()}
      viewportRef={scrollRef}
      contentClassName="px-4 [overflow-wrap:anywhere]"
    >
      {failure !== null ? (
        <KnowledgeNotice heading={failure} iconName={FAILURE_ICON} />
      ) : list.isPending ? (
        <div
          role="status"
          className="py-4 text-label text-[var(--content-fg-subtle)]"
        >
          {m.knowledge_loading()}
        </div>
      ) : items.length === 0 ? (
        <KnowledgeNotice
          heading={m.knowledge_empty_questions_heading()}
          body={m.knowledge_empty_questions_body()}
        />
      ) : (
        <Accordion
          multiple
          items={items.map((item) => ({
            value: item.id,
            title: item.question,
            content: (
              <div className="whitespace-pre-wrap [overflow-wrap:anywhere]">
                {item.answer}
              </div>
            ),
          }))}
        />
      )}
      <InfiniteScroll
        hasNextPage={list.hasNextPage && !list.isError}
        isFetchingNextPage={list.isFetchingNextPage}
        label={m.knowledge_load_more()}
        loadingLabel={m.knowledge_loading_more()}
        onLoadMore={() => {
          void list.fetchNextPage();
        }}
        rootRef={scrollRef}
      />
    </ScrollArea>
  );
}
